import { ArrowLeft20Filled } from '@fluentui/react-icons';
import { Input } from '../ui/Input';
import { useModal } from '../../hooks/useModal';
import { Button } from '../ui/Button';
import RechargeWalletAdmin from '../RechargeWalletAdmin';
import { useManageUsers } from '../../hooks/useManageUsers';
import { PencilLine } from 'lucide-react';
import { useState, useEffect } from 'react';
import { usePincodeLookup } from '../../hooks/usePincodeLookup';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

export default function UserDetail({
  userId,
  onBack,
}: {
  userId: string;
  onBack: () => void;
}) {
  const { open, close, Modal } = useModal();
  const { useUserDetailQuery, useUpdateUserMutation } = useManageUsers();
  const { data: user, isLoading } = useUserDetailQuery(userId);
  const { mutate: updateUser, isPending } = useUpdateUserMutation();
  const { mutate: lookupPincode } = usePincodeLookup();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<any>(user);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [isCompanyPincodeFailed, setIsCompanyPincodeFailed] = useState(false);
  const [isOpsPincodeFailed, setIsOpsPincodeFailed] = useState(false);

  useEffect(() => {
    setFormData(user);
    setErrors({});
    setIsCompanyPincodeFailed(false);
    setIsOpsPincodeFailed(false);
  }, [user]);

  // Handle pincode lookups
  useEffect(() => {
    if (formData?.companyPincode?.length === 6 && isEditing) {
      lookupPincode(formData.companyPincode, {
        onSuccess: (data: any) => {
          if (data && data.State) {
            setFormData((prev: any) => ({
              ...prev,
              companyState: data.State,
              companyCity: data.District
            }));
            setIsCompanyPincodeFailed(false);
          } else {
            setIsCompanyPincodeFailed(true);
          }
        },
        onError: () => setIsCompanyPincodeFailed(true)
      });
    }
  }, [formData?.companyPincode, isEditing]);

  useEffect(() => {
    if (formData?.opsPincode?.length === 6 && isEditing) {
      lookupPincode(formData.opsPincode, {
        onSuccess: (data: any) => {
          if (data && data.State) {
            setFormData((prev: any) => ({
              ...prev,
              opsState: data.State,
              opsCity: data.District
            }));
            setIsOpsPincodeFailed(false);
          } else {
            setIsOpsPincodeFailed(true);
          }
        },
        onError: () => setIsOpsPincodeFailed(true)
      });
    }
  }, [formData?.opsPincode, isEditing]);

  const handleChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors((prev) => {
        const newErrs = { ...prev };
        delete newErrs[field];
        return newErrs;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (formData?.email && formData.email.trim().length > 0) {
      if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Valid Email is required';
    }

    if (formData?.loginEmail && formData.loginEmail.trim().length > 0) {
      if (!/\S+@\S+\.\S+/.test(formData.loginEmail)) newErrors.loginEmail = 'Valid login email is required';
    }
    if (formData?.opsEmail && formData.opsEmail.trim().length > 0) {
      if (!/\S+@\S+\.\S+/.test(formData.opsEmail)) newErrors.opsEmail = 'Valid ops email is required';
    }

    const validatePhone = (field: string, label: string) => {
      const val = formData?.[field] as string;
      if (val && val.trim().length > 0) {
        if (!/^[6-9]\d{9}$/.test(val)) newErrors[field] = `Valid 10-digit ${label}`;
      }
    };

    validatePhone('mobileNo', 'Mobile Number');
    validatePhone('companyPhoneNumber', 'Company Phone Number');
    validatePhone('companyWhatsappNumber', 'Company Whatsapp');
    validatePhone('opsPhoneNumber', 'Ops Phone Number');
    validatePhone('opsWhatsappNumber', 'Ops Whatsapp');

    if (formData?.companyPincode && formData.companyPincode.trim().length > 0) {
      if (formData.companyPincode.length !== 6) newErrors.companyPincode = 'Pincode must be 6 digits';
    }

    if (formData?.opsPincode && formData.opsPincode.trim().length > 0) {
      if (formData.opsPincode.length !== 6) newErrors.opsPincode = 'Pincode must be 6 digits';
    }

    if (formData?.websiteUrl && formData.websiteUrl.trim().length > 0) {
      const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
      if (!urlRegex.test(formData.websiteUrl)) newErrors.websiteUrl = 'Invalid URL format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const EDITABLE_FIELDS = [
    'firstName', 'lastName', 'email', 'mobileNo', 
    'legalBusinessName', 'loginEmail', 'companyAddress', 'websiteUrl', 
    'brandName', 'gstNumber', 'companyPhoneNumber', 'companyPincode', 
    'companyCity', 'companyState', 'companyWhatsappNumber',
    'opsContactPerson', 'opsPhoneNumber', 'opsPincode', 'opsCity', 
    'opsState', 'opsEmail', 'opsWhatsappNumber', 'opsStoreAddress'
  ];

  const handleSave = () => {
    if (!validateForm()) {
      toast.error('Please fix validation errors');
      return;
    }

    const changes: { field: string, old: string, new: string }[] = [];
    EDITABLE_FIELDS.forEach(key => {
      const oldVal = String(user?.[key] || '').trim();
      const newVal = String(formData?.[key] || '').trim();
      if (oldVal !== newVal && !(oldVal === '' && newVal === '')) {
         changes.push({ field: key, old: oldVal, new: newVal });
      }
    });

    if (changes.length === 0) {
      toast.error('No changes detected');
      return;
    }

    open({
      Component: () => (
        <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
          <h2 className="text-xl font-bold text-text mb-4">Confirm Changes</h2>
          <p className="text-sm text-text3 mb-4">You are about to update the following fields:</p>
          <div className="max-h-60 overflow-y-auto space-y-2 mb-6 pr-2">
            {changes.map((c, i) => (
              <div key={i} className="text-sm border-b pb-2">
                <span className="font-semibold capitalize block mb-1 text-text2">
                  {c.field.replace(/([A-Z])/g, ' $1').trim()}
                </span>
                <div className="flex items-center gap-2 overflow-hidden">
                  <span className="text-red-500 line-through truncate w-[45%]" title={c.old || '(Empty)'}>{c.old || '(Empty)'}</span>
                  <span className="w-[10%] text-center">→</span>
                  <span className="text-green-600 font-medium truncate w-[45%]" title={c.new || '(Empty)'}>{c.new || '(Empty)'}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-4">
            <Button variant="outline" className="flex-1 border-gray-300" onClick={close}>Cancel</Button>
            <Button className="flex-1" onClick={() => {
              close();
              updateUser(
                { userId: userId, payload: formData },
                {
                  onSuccess: () => {
                    setIsEditing(false);
                    toast.success('Profile updated successfully');
                  },
                  onError: () => {
                    toast.error('Failed to update profile');
                  }
                }
              );
            }}>Confirm Save</Button>
          </div>
        </div>
      ),
      centered: true,
      closable: true,
      className: 'w-full max-w-md bg-transparent shadow-none',
    });
  };

  const handleRechargeWallet = () => {
    navigate(`/admin/wallet-transaction?crn=${user?.crnNumber || ''}`);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-4">
        <button
          onClick={onBack}
          className="flex items-center gap-4 text-lg font-bold text-text"
        >
          <ArrowLeft20Filled /> Back
        </button>
        <div className="flex flex-wrap gap-4 items-center">
          <Button
            variant={isEditing ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              if (isEditing) {
                setFormData(user);
                setErrors({});
                setIsCompanyPincodeFailed(false);
                setIsOpsPincodeFailed(false);
              }
              setIsEditing(!isEditing);
            }}
            className="flex items-center gap-2"
          >
            <PencilLine className="w-4 h-4" />
            {isEditing ? 'Editing Mode' : 'Edit Profile'}
          </Button>

          <Button
            onClick={handleRechargeWallet}
            className="shadow-md py-2 px-4 font-bold text-white text-lg"
          >
            Recharge
          </Button>
          <div className="px-4 py-2 bg-teal shadow-md rounded-lg">
            <p className="text-white font-bold">CRN: {user?.crnNumber || 'Loading...'}</p>
          </div>
          <div className={`px-4 py-2 shadow-md border rounded-lg ${user?.active === 1 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <p className={`font-bold ${user?.active === 1 ? 'text-green-700' : 'text-red-600'}`}>{user?.active === 1 ? 'Active' : 'Inactive'}</p>
          </div>
          <div className="px-4 py-2 shadow-md bg-white border border-border rounded-lg">
            <p className="text-text font-semibold">Available Limit: {user?.walletBalance || 'N/A'}</p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl p-20 text-center shadow">
          Loading user details...
        </div>
      ) : (
        <div className="bg-white rounded-xl p-6 shadow space-y-10 max-h-[80vh] overflow-y-auto">
          {/* Primary User Information Section */}
          <div>
            <h3 className="text-xl font-bold mb-4">Primary User Information</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <Input disabled={!isEditing} error={errors.firstName} value={formData?.firstName || ''} onChange={(e) => handleChange('firstName', e.target.value)} label="First Name" />
                <Input disabled={!isEditing} error={errors.email} value={formData?.email || ''} onChange={(e) => handleChange('email', e.target.value)} label="Primary Email" />
              </div>
              <div className="space-y-4">
                <Input disabled={!isEditing} error={errors.lastName} value={formData?.lastName || ''} onChange={(e) => handleChange('lastName', e.target.value)} label="Last Name" />
                <Input disabled={!isEditing} error={errors.mobileNo} value={formData?.mobileNo || ''} onChange={(e) => handleChange('mobileNo', e.target.value)} label="Mobile Number" />
              </div>
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* Company Information Section */}
          <div>
            <div className="flex flex-row gap-12 mb-2 items-center">
              <h3 className="text-xl font-bold mb-4">Company Information</h3>

            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <Input disabled={!isEditing} error={errors.legalBusinessName} value={formData?.legalBusinessName || ''} onChange={(e) => handleChange('legalBusinessName', e.target.value)} label="Legal Name Of Business" />
                <Input disabled={!isEditing} error={errors.loginEmail} value={formData?.loginEmail || ''} onChange={(e) => handleChange('loginEmail', e.target.value)} label="Business Login Email" />
                <Input disabled={!isEditing} error={errors.companyAddress} value={formData?.companyAddress || ''} onChange={(e) => handleChange('companyAddress', e.target.value)} label="Company Address" />
                <Input disabled={!isEditing} error={errors.websiteUrl} value={formData?.websiteUrl || ''} onChange={(e) => handleChange('websiteUrl', e.target.value)} label="Website URL" />
              </div>
              <div className="space-y-4">
                <Input disabled={!isEditing} error={errors.brandName} value={formData?.brandName || ''} onChange={(e) => handleChange('brandName', e.target.value)} label="Brand / Store Name" />
                <Input disabled={!isEditing} error={errors.gstNumber} value={formData?.gstNumber || ''} onChange={(e) => handleChange('gstNumber', e.target.value)} label="GST Number" />
                <Input disabled={!isEditing} error={errors.companyPhoneNumber} value={formData?.companyPhoneNumber || ''} onChange={(e) => handleChange('companyPhoneNumber', e.target.value)} label="Company Phone Number" />
                <Input disabled={!isEditing} error={errors.companyPincode} value={formData?.companyPincode || ''} onChange={(e) => handleChange('companyPincode', e.target.value)} label="Pincode" />
                <div className="grid grid-cols-2 gap-4">
                  <Input disabled={!isEditing || !isCompanyPincodeFailed} error={errors.companyCity} value={formData?.companyCity || ''} onChange={(e) => handleChange('companyCity', e.target.value)} label="City" />
                  <Input disabled={!isEditing || !isCompanyPincodeFailed} error={errors.companyState} value={formData?.companyState || ''} onChange={(e) => handleChange('companyState', e.target.value)} label="State" />
                </div>
                <Input disabled={!isEditing} error={errors.companyWhatsappNumber} value={formData?.companyWhatsappNumber || ''} onChange={(e) => handleChange('companyWhatsappNumber', e.target.value)} label="Company WhatsApp Number" />
              </div>
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* Operations Information Section */}
          <div>
            <h3 className="text-xl font-bold mb-4">Operations Information</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <Input disabled={!isEditing} error={errors.opsContactPerson} value={formData?.opsContactPerson || ''} onChange={(e) => handleChange('opsContactPerson', e.target.value)} label="Contact Person" />
                <Input disabled={!isEditing} error={errors.opsPhoneNumber} value={formData?.opsPhoneNumber || ''} onChange={(e) => handleChange('opsPhoneNumber', e.target.value)} label="Phone Number" />
                <Input disabled={!isEditing} error={errors.opsPincode} value={formData?.opsPincode || ''} onChange={(e) => handleChange('opsPincode', e.target.value)} label="Pincode" />
                <div className="grid grid-cols-2 gap-4">
                  <Input disabled={!isEditing || !isOpsPincodeFailed} error={errors.opsCity} value={formData?.opsCity || ''} onChange={(e) => handleChange('opsCity', e.target.value)} label="City" />
                  <Input disabled={!isEditing || !isOpsPincodeFailed} error={errors.opsState} value={formData?.opsState || ''} onChange={(e) => handleChange('opsState', e.target.value)} label="State" />
                </div>
              </div>
              <div className="space-y-4">
                <Input disabled={!isEditing} error={errors.opsEmail} value={formData?.opsEmail || ''} onChange={(e) => handleChange('opsEmail', e.target.value)} label="Email" />
                <Input disabled={!isEditing} error={errors.opsWhatsappNumber} value={formData?.opsWhatsappNumber || ''} onChange={(e) => handleChange('opsWhatsappNumber', e.target.value)} label="WhatsApp Number" />
                <Input disabled={!isEditing} error={errors.opsStoreAddress} value={formData?.opsStoreAddress || ''} onChange={(e) => handleChange('opsStoreAddress', e.target.value)} label="Store Address" />
              </div>
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* Bank Information Section */}
          <div>
            <h3 className="text-xl font-bold mb-4">Bank Information (Read-Only)</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <Input disabled value={user?.accountHolderName || ''} label="Account Holder Name" />
                <Input disabled value={user?.bankName || ''} label="Bank Name" />
                <Input disabled value={user?.accountNumber || ''} label="Account Number" />
              </div>
              <div className="space-y-4">
                <Input disabled value={user?.ifscCode || ''} label="IFSC Code" />
                <Input disabled value={user?.branch || ''} label="Branch" />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          {isEditing && (
            <div className="flex pt-6">
              <Button
                onClick={handleSave}
                size="lg"
                className="px-8 py-3 text-base font-medium"
                disabled={isPending}
              >
                {isPending ? 'Saving...' : 'Save Profile'}
              </Button>
            </div>
          )}
        </div>
      )}
      <Modal />
    </div>
  );
}
